import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useImageViewerStore } from "@mirage/store/store";
import { Button, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Field from "../regularField/Field";

export const ImageViewer = () => {
    const state = useImageViewerStore((s) => s.imageViewer);
    const [visible, setVisible] = useState(false);
    const [localOptions, setLocalOptions] = useState(state.options);
    const { base64, onClose, options } = state;
    const [scale, setScale] = useState(1);
    const [lastZoom, setLastZoom] = useState(0);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragging = useRef(false);
    const lastMousePos = useRef({ x: 0, y: 0 });
    const lastOffset = useRef({ x: 0, y: 0 });

    useEffect(() => {
        setVisible(!!base64);
        setLocalOptions(options);
        setDescription(options?.description || "");
    }, [base64, options]);

    const className = visible ? "" : "container--hidden";
    const fullWindowModifier = "popupContainer--fullWindow";
    const viewerStyle = localOptions ? { width: "calc(100% - 300px)" } : ({ width: "100%" } as React.CSSProperties);
    viewerStyle.backgroundColor = "#f2f2f2";
    viewerStyle.overflow = "hidden";
    viewerStyle.cursor = dragging.current ? "grabbing" : "grab";

    const closeButton = (
        <Button variant="outlined" onClick={onCloseClicked}>
            Close
        </Button>
    );
    const saveButton =
        localOptions && localOptions.readonly ? null : (
            <Button variant="contained" onClick={onSaveClicked}>
                Save
            </Button>
        );
    const [description, setDescription] = useState(localOptions?.description || "");
    const isReadonly = localOptions?.readonly || false;
    const detailsPanel = localOptions ? (
        <div style={{ zIndex: 100, width: "300px", padding: "10px", backgroundColor: "#fff", boxShadow: "-11px 0px 23px -16px rgba(66, 68, 90, 1)" }}>
            <Field containerClassName="miragePanelFieldContainer" label="Name" value={localOptions.name} mode="view" contentType="TEXT" />
            <Field containerClassName="miragePanelFieldContainer" label="Last modified" value={localOptions.lastModified} mode="view" contentType="TEXT" />
            <Field containerClassName="miragePanelFieldContainer" label="Size" value={localOptions.size} mode="view" contentType="TEXT" />
            <Field containerClassName="miragePanelFieldContainer" label="Creator" value={localOptions.creator} mode="view" contentType="TEXT" />
            <Field
                containerClassName="miragePanelFieldContainer"
                label="Description"
                value={description}
                onChange={(e, value) => {
                    setDescription(value);
                }}
                mode={isReadonly ? "view" : "edit"}
                contentType={"TEXTAREA"}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                {saveButton}
                {closeButton}
            </div>
        </div>
    ) : null;

    const divStyle = {
        width: "100%",
        height: "100%",
        backgroundImage: `url(${base64})`,
        backgroundPosition: "center",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
        transition: dragging.current ? "none" : "transform 0.2s ease",
    } as React.CSSProperties;
    const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        dragging.current = true;
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        lastOffset.current = { ...offset };
    };
    const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (!dragging.current) {
            return;
        }
        e.preventDefault();
        const dx = e.clientX - lastMousePos.current.x;
        const dy = e.clientY - lastMousePos.current.y;
        setOffset({
            x: lastOffset.current.x + dx,
            y: lastOffset.current.y + dy,
        });
    };
    const onMouseUpOrLeave: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (dragging.current) {
            e.preventDefault();
            dragging.current = false;
        }
    };

    const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        const currentTime = new Date().getTime();
        const timeDiff = currentTime - lastZoom;
        let sensitivity = 0.001;
        if (timeDiff < 100) {
            sensitivity = 0.005;
        }
        if (timeDiff < 50) {
            sensitivity = 0.01;
        }
        let newScale = scale - e.deltaY * sensitivity;
        newScale = Math.min(Math.max(newScale, 0.125), 4);
        setScale(newScale);
        setLastZoom(new Date().getTime());
    };

    return (
        <div className={`popupContainer ${fullWindowModifier} ${className}`}>
            <div className="container__toolBar container__toolBar--rightAligned">
                <Tooltip title="Close">
                    <FontAwesomeIcon className="container__pointerIcon" icon={faXmark} onClick={onCloseClicked} />
                </Tooltip>
            </div>
            <div className="container--fullHeight" style={{ display: "flex" }}>
                <div style={viewerStyle}>
                    <div onWheel={onWheel} style={divStyle} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUpOrLeave} onMouseLeave={onMouseUpOrLeave}></div>
                </div>
                {detailsPanel}
            </div>
        </div>
    );
    function onCloseClicked() {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        onClose();
    }
    function onSaveClicked() {
        onClose(description);
    }
};
